import { AuthType, HorizontalAlignment, LogManager, VerticalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, ButtonVariant, HorizontalSpacer, Image, Label, Layout, LayoutProps, Panel } from "@mcleod/components";
import { CapacitorAccess } from "../capacitor";
import { AppBackButtonHandler } from "../navigation";

const log = LogManager.getLogger("common.AppConnectionErrorLayout");

export interface AppConnectionErrorLayoutProps extends LayoutProps {
    /** Shown as the headline.  Defaults to a generic "can't reach the server" message. */
    summary: string;
    /** Shown under the headline in a quieter style. */
    detail: string;
    /** Called when the user taps "Try again".  Defaults to a full reload of the current document. */
    onRetry: () => void;
    /**
     * Called when the user taps "Change environment".  Defaults to asking the native OTA plugin to reload the
     * built-in shell bundle; the button is hidden when that is not possible (web, or no OTA plugin present).
     */
    onChangeEnvironment: () => void;
}

/**
 * Mobile replacement for the desktop connectivity error page.
 *
 * The desktop version is a static illustration with no controls, which on a phone leaves the user stranded inside
 * an OTA bundle that cannot reach its API with no way back to the environment list short of force-quitting.  This
 * layout keeps the message short, uses the theme, respects the safe areas, and always offers a way forward.
 */
export class AppConnectionErrorLayout extends Layout {
    private readonly _summary: string;
    private readonly _detail: string;
    private readonly _onRetry: () => void;
    private readonly _onChangeEnvironment: (() => void) | null;

    constructor(props?: Partial<AppConnectionErrorLayoutProps>) {
        super({
            id: "appConnectionError",
            auth: AuthType.UNAUTH,
            needsServerLayout: false,
            fillRow: true,
            fillHeight: true,
            padding: 24,
            align: HorizontalAlignment.CENTER,
            verticalAlign: VerticalAlignment.CENTER,
            backgroundColor: "defaultBackground",
            ...AppConnectionErrorLayout.stripOwnProps(props)
        });
        this._summary = props?.summary ?? "Can't reach the server";
        this._detail = props?.detail
            ?? "The server may be unavailable or your device may be offline. Check your connection and try again.";
        this._onRetry = props?.onRetry ?? (() => window.location.reload());
        this._onChangeEnvironment = props?.onChangeEnvironment ?? AppConnectionErrorLayout.defaultChangeEnvironment();
        this.style.paddingTop = "calc(24px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))";
        this.style.paddingBottom = "calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))";
        this.addComponents();
        this.registerBackHandling();
    }

    /**
     * On Android, pressing back on this screen means "get me out of this bundle", so it behaves like the
     * "Change environment" action when that is available.
     */
    private registerBackHandling(): void {
        if (this._onChangeEnvironment == null)
            return;
        let removeInterceptor: (() => void) | null = null;
        this.addMountListener(() => {
            removeInterceptor = AppBackButtonHandler.pushInterceptor(() => {
                this._onChangeEnvironment?.();
                return true;
            });
        });
        this.addUnmountListener(() => {
            removeInterceptor?.();
            removeInterceptor = null;
        });
    }

    private static stripOwnProps(props?: Partial<AppConnectionErrorLayoutProps>): Partial<LayoutProps> {
        if (props == null)
            return {};
        const { summary, detail, onRetry, onChangeEnvironment, ...layoutProps } = props;
        return layoutProps;
    }

    /**
     * Returns a handler that reloads the built-in shell bundle when the host app's OTA plugin offers it, else null
     * so the "Change environment" action is not shown at all.
     */
    private static defaultChangeEnvironment(): (() => void) | null {
        const otaPlugin = CapacitorAccess.plugins?.OTAPlugin;
        if (!CapacitorAccess.isNativePlatform || typeof otaPlugin?.reloadBuiltIn !== "function")
            return null;
        return () => {
            otaPlugin.reloadBuiltIn().catch((error: unknown) => log.error("reloadBuiltIn failed", error));
        };
    }

    private addComponents(): void {
        const content = Panel.createNoMarginNoPaddingPanel({
            id: "panelContent",
            width: "100%",
            maxWidth: 420,
            rowBreak: true,
            align: HorizontalAlignment.CENTER
        });
        // Flexible spacers above and below keep the message in the vertical middle of whatever space is left.
        const spacer = () => new HorizontalSpacer({ fillHeight: true, fillRow: true, rowBreak: true });
        content.add(
            new Image({ name: ImageName.circleError, height: 72, width: 72, color: "subtle", marginBottom: 16 }),
            new Label({
                id: "labelSummary",
                text: this._summary,
                fontSize: "xxlarge",
                fontBold: true,
                align: HorizontalAlignment.CENTER,
                fillRow: true,
                allowSelect: false,
                marginBottom: 8
            }),
            new Label({
                id: "labelDetail",
                text: this._detail,
                fontSize: "medium",
                color: "subtle.darker",
                align: HorizontalAlignment.CENTER,
                fillRow: true,
                allowSelect: false,
                marginBottom: 24
            }),
            new Button({
                id: "buttonRetry",
                caption: "Try again",
                color: "primary.reverse",
                backgroundColor: "primary",
                fontBold: true,
                fontSize: "large",
                fillRow: true,
                width: "100%",
                height: 50,
                borderWidth: 0,
                borderRadius: 12,
                margin: 0,
                marginBottom: 12,
                onClick: () => this._onRetry()
            })
        );
        if (this._onChangeEnvironment != null) {
            content.add(new Button({
                id: "buttonChangeEnvironment",
                caption: "Change environment",
                variant: ButtonVariant.text,
                color: "primary",
                fontSize: "large",
                fillRow: true,
                width: "100%",
                height: 44,
                margin: 0,
                onClick: () => this._onChangeEnvironment?.()
            }));
        }
        this.add(spacer(), content, spacer());
    }
}
